import React, { useEffect, useState, useCallback } from "react";
import { Loader2, CreditCard, CheckCircle2, ExternalLink, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const FEATURES = [
  "Unlimited documents, reviewed by Dox",
  "Real-time compliance monitoring",
  "Renewal package generator (PDF filings)",
  "Business memory + verification mode",
  "Daily briefing + iCal compliance calendar",
  "Team roles & audit logs (SOC2-ready)",
  "Multi-business Accountant Mode",
];

export default function Billing() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/subscription/status");
      setStatus(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      setLoading(false);
      toast.error("Couldn't start checkout. Try again in a moment.");
    }
  };

  const sub = status?.subscription;
  const active = sub && ["active", "trialing"].includes(sub.status);

  return (
    <div className="max-w-4xl space-y-6" data-testid="billing-view">
      <div className="glass-card p-7 sm:p-9">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-dox-orange" />
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-dox-magenta">Billing</div>
        </div>
        <h2 className="font-display font-black text-3xl sm:text-4xl">
          {active ? "You're all set." : "Hire Dox for your business."}
        </h2>
        <p className="text-white/60 mt-3 max-w-xl">
          {active
            ? "Dox is on the clock for your business. Manage your plan below."
            : "Start with a 14-day free trial. Cancel any time. Dox pays for itself the first time it prevents a missed filing."}
        </p>

        {active ? (
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            <div className="glass-card p-4">
              <div className="text-xs tracking-widest uppercase text-white/40">Plan</div>
              <div className="mt-1 text-white font-semibold text-lg">$99 <span className="text-white/40 text-sm">/business/mo</span></div>
            </div>
            <div className="glass-card p-4">
              <div className="text-xs tracking-widest uppercase text-white/40">Status</div>
              <div className="mt-1 text-emerald-400 font-semibold text-lg capitalize">{sub.status}</div>
            </div>
            <div className="glass-card p-4">
              <div className="text-xs tracking-widest uppercase text-white/40">Since</div>
              <div className="mt-1 text-white font-semibold text-lg">
                {sub.activated_at ? new Date(sub.activated_at).toLocaleDateString() : "—"}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div className="glass-card p-6 border-dox-orange/40">
              <div className="text-xs tracking-widest uppercase text-dox-orange font-bold">14-day free trial</div>
              <div className="mt-2 flex items-baseline gap-2">
                <div className="font-display font-black text-5xl">$99</div>
                <div className="text-white/40 text-sm">/business/month</div>
              </div>
              <div className="mt-2 text-white/55 text-sm">Billed monthly, cancel any time. First 14 days free.</div>
              <button
                data-testid="billing-checkout-btn"
                onClick={startCheckout}
                disabled={loading}
                className="mt-6 w-full rounded-full bg-dox-orange px-6 py-3.5 text-white font-bold text-base flex items-center justify-center gap-2 hover:bg-dox-orangeHover disabled:opacity-60 transition-colors"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                {loading ? "Redirecting to Stripe…" : "Start free trial"}
              </button>
              <div className="mt-3 flex items-center gap-2 text-xs text-white/45">
                <ShieldCheck size={12} className="text-emerald-400" /> Secured by Stripe · Test card 4242 4242 4242 4242
              </div>
            </div>
            <div className="glass-card p-6">
              <div className="text-sm font-semibold text-white/85 mb-3">Everything included</div>
              <ul className="space-y-2.5">
                {FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-white/70">
                    <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      {status?.onboarding_url && (
        <a
          href={status.onboarding_url}
          target="_blank"
          rel="noreferrer"
          className="glass-card p-5 flex items-center justify-between hover:border-white/20 transition-colors"
          data-testid="stripe-claim-link"
        >
          <div>
            <div className="text-sm text-white/85 font-semibold">Payments running in Stripe sandbox</div>
            <div className="text-xs text-white/50 mt-1">Claim this Stripe account when you're ready to go live — no code changes needed.</div>
          </div>
          <ExternalLink size={16} className="text-white/60" />
        </a>
      )}
    </div>
  );
}
